import _ from "lodash";
import { memo, useCallback, useEffect, useMemo } from "react";
import Timer from "../../../Img/Timer.svg";
import { useDispatch, useSelector } from "react-redux";
import { Button, DatePicker, Form, Input, Modal, Select } from "antd";
import {
  addReminder,
  getScheduleList,
  getSearchPatientList,
  setEditReminderData,
  setIsEditReminderModal,
  setSearchPatientList,
  setShowReminderModal,
  updateReminder,
} from "redux/reducers/Dashboard/dashboard.slice";
import dayjs from "dayjs";
import moment from "moment";
import { getNotificationList } from "redux/reducers/Notification/NotificationSlice";

const reminderFormInitialValues = {
  title: "",
  description: "",
  reminder_date: null,
  patient_reg_id: null,
};

const ReminderModal = () => {
  const { TextArea } = Input;
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const theme = localStorage.getItem("default-theme");
  const { selectedLocation } = useSelector(({ role }) => role);
  const {
    showReminderModal,
    searchPatientList,
    isEditReminderModal,
    editReminderData,
    scheduleStartPage,
    scheduleDate,
    scheduleObject,
  } = useSelector(({ dashboard }) => dashboard);
  const { notificationPageLimit, notificationCurrentPage } = useSelector(
    ({ notification }) => notification
  );

  const handlePatientSearch = useCallback(
    (newValue) => {
      if (newValue) {
        const payload = { patient_name: newValue };

        dispatch(
          getSearchPatientList({
            start: null,
            limit: null,
            payload: payload,
          })
        );
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const hasEditData =
      editReminderData && Object.keys(editReminderData).length > 0;

    if (!hasEditData || !isEditReminderModal) return;

    const { patient_reg_id, patient_full_name, reminder_date } =
      editReminderData;

    const formPatientId = form.getFieldValue("patient_reg_id");

    const shouldUpdate =
      formPatientId !== patient_reg_id || patient_reg_id === null;

    if (shouldUpdate) {
      handlePatientSearch(patient_full_name);

      form.setFieldsValue({
        ...editReminderData,
        reminder_date: reminder_date ? dayjs(reminder_date) : null,
      });
    }
  }, [isEditReminderModal, editReminderData, handlePatientSearch, form]);

  const callGetScheduleList = useCallback(
    (start, limit, locationId, date, prevData = {}) => {
      dispatch(
        getScheduleList({
          start,
          limit,
          location_id: locationId,
          date: moment(date).format("YYYY-MM-DD"),
          prevData,
        })
      );
    },
    [dispatch]
  );

  const onFinish = async (values) => {
    const payload = {
      ...values,
      reminder_date: values.reminder_date.toISOString(),
    };

    if (isEditReminderModal) {
      dispatch(
        updateReminder({
          location_id: selectedLocation,
          id: isEditReminderModal,
          payload,
        })
      );
    } else {
      const res = await dispatch(
        addReminder({ location_id: selectedLocation, payload })
      );

      if (res?.payload?.data) {
        dispatch(
          getNotificationList({
            location_id: selectedLocation,
            start: notificationCurrentPage,
            limit: notificationPageLimit,
          })
        );
      }
    }

    handleClose();

    callGetScheduleList(
      scheduleStartPage,
      10,
      selectedLocation,
      scheduleDate,
      scheduleObject
    );
  };

  const onFinishFailed = (errorInfo) => {
    console.error("Failed:", errorInfo);
  };

  const clearSearchPatientList = () => {
    dispatch(setSearchPatientList([]));
  };

  const handleClose = () => {
    form.resetFields();
    dispatch(setShowReminderModal(false));
    dispatch(setIsEditReminderModal(null));
    dispatch(setEditReminderData({}));
    clearSearchPatientList();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncePatientSearchInput = useCallback(
    _.debounce(handlePatientSearch, 800),
    []
  );

  const patientOptions = useMemo(() => {
    return searchPatientList || [];
  }, [searchPatientList]);

  return (
    <Modal
      title="Reminder"
      centered
      open={showReminderModal}
      className={`reminderModal theme-${theme}`}
      onCancel={handleClose}
    >
      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={reminderFormInitialValues}
      >
        <div className="form_process_wrapper p-0">
          <div>
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: "" }]}
            >
              <Input placeholder="Title" name="title" />
            </Form.Item>
          </div>

          <div>
            <Form.Item label="Description" name="description">
              <TextArea rows={4} placeholder="Description" />
            </Form.Item>
          </div>

          <div>
            <Form.Item
              label="Date"
              name="reminder_date"
              rules={[{ required: true, message: "" }]}
            >
              <DatePicker showTime />
            </Form.Item>
          </div>

          <div>
            <Form.Item
              label="Patient"
              name="patient_reg_id"
              className="custom_select"
              rules={[
                {
                  required: false,
                  message: "",
                },
              ]}
            >
              <Select
                showSearch
                name="patient_reg_id"
                placeholder="Search Patient"
                defaultActiveFirstOption={false}
                suffixIcon={null}
                filterOption={false}
                allowClear
                onClear={clearSearchPatientList}
                onSearch={(e) => debouncePatientSearchInput(e)}
                notFoundContent={null}
                options={patientOptions}
              />
            </Form.Item>
          </div>
        </div>

        <div className="d-flex justify-content-center gap-2">
          <Button className="btn_primary reminder_btn" htmlType="submit">
            <img src={Timer} alt="" />
            Save
          </Button>

          <Button className="btn_gray close_btn" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default memo(ReminderModal);
